import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Divider, 
  Container, 
  TextField,
  InputAdornment,
  Button,
  Tab,
  Tabs,
  Grid,
  IconButton,
  Badge,
  useTheme,
  Fade,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import ProfileImageUpload from '../components/ProfileImageUpload';
import CallCard from '../components/CallCard';
import { hentOpkaldsData, hentBrugerInternId } from '../services/firebase_datahent';
import { storage } from '../services/firebase';
import { ref, getDownloadURL, listAll, getMetadata } from 'firebase/storage';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';

// Styled components for layout
const AppContainer = styled(Box)({
  display: 'flex',
  height: '100%',
  backgroundColor: '#f8f9fa'
});

const Sidebar = styled(Box)(({ theme }) => ({
  width: '150px',
  backgroundColor: 'white',
  borderRight: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2, 0),
  boxShadow: '0 0 15px rgba(0,0,0,0.05)',
}));

const MenuItemContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(1.5),
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'rgba(25, 118, 210, 0.08)',
    color: theme.palette.primary.main,
  },
  transition: 'all 0.2s ease',
}));

const MenuItemText = styled(Typography)({
  fontSize: '0.85rem',
  color: '#666',
  transition: 'color 0.2s ease',
});

const MainContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(0),
  backgroundColor: '#f8f9fa',
  overflow: 'auto',
  height: '100vh',
}));

const Header = styled(Box)(({ theme }) => ({
  backgroundColor: 'white',
  color: '#334155',
  padding: theme.spacing(1, 2),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
}));

const ContentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 5),
  maxWidth: '1400px',
  margin: '0 auto',
}));

// Styled components specefik for Calls page
const PageTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  marginBottom: theme.spacing(0.5),
  background: 'linear-gradient(90deg, #1976d2, #64b5f6)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
}));

const FilterButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: '8px',
}));

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    fontWeight: 'bold',
  },
}));

// Interface for call data
interface CallData {
  id: number | string;
  customer: string;
  date: string;
  time: string;
  timestamp: number;  // Tilføj timestamp for sortering
  duration: string;
  talkRatio: number;
  authority: number;
  toneVariation: number;
  energyVariation: number;
  audioUrl?: string;
  segments: {
    name: string;
    score: number;
    color: string;
  }[];
  actionPoints?: {
    toneVariation?: string;
    talkTime?: string;
  };
}

// Interface for Firebase opkaldsdata
interface FirebaseCallData {
  id: string;
  user_id?: string;
  timestamp?: {
    seconds: number;
    nanoseconds: number;
  } | string | number;
  call_duration?: number;
  tlr?: number;  // Talk-listen ratio (procentdel af tiden agenten taler)
  action_punkt_1?: string;
  action_punkt_2?: string;
  observed_values?: {
    dominance_mean?: number;
    pitch_std?: number;
    energy_std?: number;
    [key: string]: any;
  };
  [key: string]: any;
}

// Mock data som backup
const mockCalls: CallData[] = [
  {
    id: 1,
    customer: "Opkald #10051",
    date: "2025-05-10",
    time: "10:15",
    timestamp: new Date("2025-05-10T10:15:00").getTime(),
    duration: "8:23",
    talkRatio: 0.45,
    authority: 72,
    toneVariation: 68,
    energyVariation: 58,
    audioUrl: '/dummy_audio.mp3',
    segments: [
      { name: "Intro", score: 82, color: "#4285F4" },
      { name: "Midte", score: 65, color: "#9B87F5" },
      { name: "Slutning", score: 74, color: "#6E56DB" }
    ]
  },
  {
    id: 2,
    customer: "Opkald #10052",
    date: "2025-05-09",
    time: "13:40",
    timestamp: new Date("2025-05-09T13:40:00").getTime(),
    duration: "12:05",
    talkRatio: 0.38,
    authority: 64,
    toneVariation: 71,
    energyVariation: 61,
    audioUrl: '/dummy_audio.mp3',
    segments: [
      { name: "Intro", score: 67, color: "#4285F4" },
      { name: "Midte", score: 73, color: "#9B87F5" },
      { name: "Slutning", score: 81, color: "#6E56DB" }
    ]
  },
  {
    id: 3,
    customer: "Opkald #10053",
    date: "2025-05-08",
    time: "15:22",
    timestamp: new Date("2025-05-08T15:22:00").getTime(),
    duration: "5:46",
    talkRatio: 0.52,
    authority: 69,
    toneVariation: 62,
    energyVariation: 65,
    audioUrl: '/dummy_audio.mp3',
    segments: [
      { name: "Intro", score: 75, color: "#4285F4" },
      { name: "Midte", score: 67, color: "#9B87F5" },
      { name: "Slutning", score: 59, color: "#6E56DB" }
    ]
  },
  {
    id: 4,
    customer: "Opkald #10054",
    date: "2025-05-07",
    time: "09:10",
    timestamp: new Date("2025-05-07T09:10:00").getTime(),
    duration: "10:33",
    talkRatio: 0.41,
    authority: 78,
    toneVariation: 74,
    energyVariation: 66,
    audioUrl: '/dummy_audio.mp3',
    segments: [
      { name: "Intro", score: 84, color: "#4285F4" },
      { name: "Midte", score: 78, color: "#9B87F5" },
      { name: "Slutning", score: 82, color: "#6E56DB" }
    ]
  },
  {
    id: 5,
    customer: "Opkald #10055",
    date: "2025-05-06",
    time: "14:05",
    timestamp: new Date("2025-05-06T14:05:00").getTime(),
    duration: "7:19",
    talkRatio: 0.47,
    authority: 63,
    toneVariation: 59,
    energyVariation: 54,
    audioUrl: '/dummy_audio.mp3',
    segments: [
      { name: "Intro", score: 61, color: "#4285F4" },
      { name: "Midte", score: 63, color: "#9B87F5" },
      { name: "Slutning", score: 68, color: "#6E56DB" }
    ]
  }
];

// Tilføj en ny type til sorteringsretning
type SortDirection = 'asc' | 'desc';

const Calls: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser, userName } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  
  // States
  const [searchQuery, setSearchQuery] = useState("");
  const [calls, setCalls] = useState<CallData[]>([]);
  const [filteredCalls, setFilteredCalls] = useState<CallData[]>([]);
  const [timeFilter, setTimeFilter] = useState("all");
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [debugOutput, setDebugOutput] = useState<string[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<'date' | 'score'>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [playingCallId, setPlayingCallId] = useState<number | string | null>(null);
  // Pagination
  const [visibleCount, setVisibleCount] = useState<number>(10);
  
  // Fetch data from Firebase
  useEffect(() => {
    const fetchData = async () => {
      setDebugOutput([]);
      setLoading(true); // Start loading
      
      const logToDebug = (message: string, data?: any) => {
        const entry = data ? `${message}: ${JSON.stringify(data, null, 2)}` : message;
        setDebugOutput(prev => [...prev, entry]);
        console.log(message, data);
      };

      // Funktion til at logge debug information
      const writeToLogFile = async (message: string) => {
        const timestamp = new Date().toISOString();
        const logEntry = `[${timestamp}] ${message}`;
        
        // Vis i debug output på siden
        setDebugOutput(prev => [...prev, logEntry]);
        
        // Vis i konsollen med tydelig markering
        console.log('🔥 FIREBASE DEBUG:', message);
        
        // Gem også i localStorage så vi kan downloade det
        const existingLogs = localStorage.getItem('voice-victor-debug-logs') || '';
        localStorage.setItem('voice-victor-debug-logs', existingLogs + logEntry + '\n');
      };

      logToDebug("Aktuel currentUser i Calls.tsx", currentUser);
      
      if (!currentUser) {
        logToDebug("Ingen currentUser fundet, stopper datahentning.");
        setError("Du skal være logget ind for at se opkald.");
        setLoading(false);
        return;
      }

      try {
        logToDebug("Calls: Starter hentning af bruger-ID for auth bruger", currentUser.uid);
        
        // Hent brugerens interne ID
        const internUserId = await hentBrugerInternId(currentUser.uid);
        logToDebug("Modtaget internt bruger-ID:", internUserId);
        
        if (!internUserId) {
          logToDebug("Kunne ikke finde internt bruger-ID");
          setError("Kunne ikke finde dit bruger-ID. Kontakt venligst support.");
          setLoading(false);
          return;
        }

        // Hent opkaldsdata
        const opkaldsData = await hentOpkaldsData(currentUser.uid);
        logToDebug("Rå opkaldsData fra Firebase:", opkaldsData);

        if (!opkaldsData || !Array.isArray(opkaldsData)) {
          logToDebug("Ingen opkaldsdata returneret fra Firebase eller data er ikke et array");
          setError("Kunne ikke hente dine opkaldsdata. Prøv igen senere.");
          setLoading(false);
          return;
        }

        // TEST: Med korrekt Firebase Storage bucket
        await writeToLogFile(`TEST: Bruger korrekt bucket - voice-victor.firebasestorage.app`);
        await writeToLogFile(`TEST: Tjekker soundfiles rod-mappe`);
        const soundfilesRootRef = ref(storage, 'soundfiles');
        
        try {
          const rootListResult = await listAll(soundfilesRootRef);
          await writeToLogFile(`TEST: SUCCESS - Fandt ${rootListResult.prefixes.length} undermapper i soundfiles/`);
          
          // List alle undermapper
          for (let i = 0; i < rootListResult.prefixes.length; i++) {
            const folder = rootListResult.prefixes[i];
            await writeToLogFile(`TEST: Undermappe ${i + 1}: ${folder.name}`);
          }
          
          // Nu prøv specifikt brugerens mappe (bare P44, ikke user_P44)
          await writeToLogFile(`TEST: Prøver brugerens mappe: soundfiles/${internUserId}`);
          const userSoundfilesRef = ref(storage, `soundfiles/${internUserId}`);
          const userListResult = await listAll(userSoundfilesRef);
          await writeToLogFile(`TEST: SUCCESS - Fandt ${userListResult.items.length} filer i brugerens mappe`);
          
          // List filnavne
          for (let i = 0; i < userListResult.items.length; i++) {
            const item = userListResult.items[i];
            await writeToLogFile(`TEST: Fil ${i + 1}: ${item.name}`);
          }
          
        } catch (error) {
          await writeToLogFile(`TEST: FEJL - ${error}`);
        }

        // Hent lydfiler fra brugerens mappe først
        let audioFiles: { [key: string]: string } = {};
        try {
          const userSoundfilesRef = ref(storage, `soundfiles/${internUserId}`);
          const userListResult = await listAll(userSoundfilesRef);
          
          await writeToLogFile(`AUDIO: Fandt ${userListResult.items.length} lydfiler i brugerens mappe`);
          
          // Hent download URLs for alle lydfiler
          const audioPromises = userListResult.items.map(async (item) => {
            const downloadUrl = await getDownloadURL(item);
            // Fjern .wav extension fra filnavn for at matche med call_id
            const callId = item.name.replace('.wav', '');
            await writeToLogFile(`AUDIO: Mappet ${item.name} til call_id: ${callId}`);
            await writeToLogFile(`AUDIO: Download URL: ${downloadUrl}`);
            
            // Test om URL'en er tilgængelig
            try {
              const response = await fetch(downloadUrl, { method: 'HEAD' });
              await writeToLogFile(`AUDIO: URL test for ${item.name}: ${response.status} ${response.statusText}`);
              await writeToLogFile(`AUDIO: Content-Type: ${response.headers.get('content-type')}`);
              await writeToLogFile(`AUDIO: Content-Length: ${response.headers.get('content-length')}`);
            } catch (error) {
              await writeToLogFile(`AUDIO: URL test fejl for ${item.name}: ${error}`);
            }
            
            return { callId, url: downloadUrl };
          });
          
          const audioResults = await Promise.all(audioPromises);
          audioFiles = audioResults.reduce((acc, { callId, url }) => {
            acc[callId] = url;
            return acc;
          }, {} as { [key: string]: string });
          
          await writeToLogFile(`AUDIO: Oprettede mapping for ${Object.keys(audioFiles).length} lydfiler`);
          
        } catch (error) {
          await writeToLogFile(`AUDIO: Fejl ved hentning af lydfiler - ${error}`);
        }

        // Konverter Firebase data til det format CallCard komponenten forventer (inkl. hent audio URL)
        const formattedPromises = opkaldsData.map(async (opkald: FirebaseCallData) => {
          // Konverter timestamp til Date objekt
          let dateObj = new Date();
          if (opkald.timestamp) {
            if (typeof opkald.timestamp === 'object' && 'seconds' in opkald.timestamp) {
              dateObj = new Date(opkald.timestamp.seconds * 1000);
            } else if (typeof opkald.timestamp === 'string' || typeof opkald.timestamp === 'number') {
              dateObj = new Date(opkald.timestamp as any);
            }
          }
          
          // Formater dato og tid
          const date = dateObj.toISOString().split('T')[0];
          const time = dateObj.toLocaleTimeString('da-DK', { 
            hour: '2-digit', 
            minute: '2-digit' 
          });
          
          // Beregn varighed
          const duration = opkald.call_duration 
            ? `${Math.floor(Number(opkald.call_duration) / 60)}:${String(Math.floor(Number(opkald.call_duration) % 60)).padStart(2, '0')}`
            : "0:00";
          
          // Brug tlr fra observed_values
          const talkRatio = opkald.observed_values?.tlr !== undefined ? Number(opkald.observed_values.tlr) : 0.5;

          // Match lydfil med call_id
          const callId = String(opkald.call_id || opkald.id);
          const audioUrl = audioFiles[callId];
          
          await writeToLogFile(`AUDIO: Opkald ${callId} ${audioUrl ? 'HAR' : 'MANGLER'} lydfil`);
          
          console.log('🔊 MATCHING: Opkald call_id:', callId, 'Audio URL:', audioUrl ? 'FUNDET' : 'IKKE FUNDET');

          // Log for debugging
          console.log('Talk Ratio Data:', {
            id: opkald.id,
            tlr: opkald.observed_values?.tlr,
            final_ratio: talkRatio
          });

          return {
            id: opkald.id,
            customer: `Opkald #${opkald.call_id || opkald.id}`,
            date,
            time,
            timestamp: dateObj.getTime(),
            duration,
            talkRatio,
            authority: opkald.observed_values?.dominance_mean || 50,
            toneVariation: opkald.observed_values?.pitch_std || 50,
            energyVariation: opkald.observed_values?.energy_std || 50,
            audioUrl,
            segments: [
              { name: "Intro", score: opkald.segment_scores?.intro || 50, color: "#4285F4" },
              { name: "Midte", score: opkald.segment_scores?.middle || 50, color: "#9B87F5" },
              { name: "Slutning", score: opkald.segment_scores?.end || 50, color: "#6E56DB" }
            ],
            actionPoints: {
              toneVariation: opkald.action_punkt_1 || 'Ingen handlingspunkter tilgængelige',
              talkTime: opkald.action_punkt_2 || 'Ingen handlingspunkter tilgængelige'
            }
          };
        });

        const formattedCalls: CallData[] = await Promise.all(formattedPromises);

        // Tilføj validering før vi sætter data
        if (formattedCalls.length > 0) {
          logToDebug(`Formaterede ${formattedCalls.length} opkald succesfuldt`);
          setCalls(formattedCalls);
          setFilteredCalls(formattedCalls);
          setError(null);
        } else {
          logToDebug("Ingen opkald at vise efter formatering");
          setError("Ingen opkald fundet");
        }
        setLoading(false);
      } catch (error) {
        console.error('Fejl ved hentning af data:', error);
        logToDebug('Fejl ved hentning af data:', error);
        setError('Der opstod en fejl ved hentning af dine opkald.');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [currentUser as { uid: string }]);
  
  // Handlers
  const handleNavigate = (path: string) => {
    console.log(`Navigerer til: ${path}`); // Debugging log
    if (path === 'Race') {
      navigate('/race');
    } else if (path === 'Calls') {
      navigate('/calls');
    } else if (path === 'Dashboard') {
      navigate('/agent-dashboard');
    } else if (path === 'Stats') {
      navigate('/stats');
    } else if (path === 'Training') {
      navigate('/training');
    } else {
      // Disse sider er endnu ikke implementeret
      console.log(`Side '${path}' er ikke implementeret endnu`);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setLoading(true);
    
    setTimeout(() => {
      if (query.trim() === "") {
        setFilteredCalls(calls);
      } else {
        const filtered = calls.filter(call => 
          call.customer.toLowerCase().includes(query.toLowerCase()) ||
          call.date.includes(query)
        );
        setFilteredCalls(filtered);
      }
      setLoading(false);
    }, 300);
  };

  const handleTimeFilterChange = (filter: string) => {
    setTimeFilter(filter);
    setLoading(true);
    
    setTimeout(() => {
      const now = new Date();
      const filteredData = calls.filter(call => {
        const callDate = new Date(call.date);
        
        if (filter === 'week') {
          const weekAgo = new Date();
          weekAgo.setDate(now.getDate() - 7);
          return callDate >= weekAgo;
        } else if (filter === 'month') {
          const monthAgo = new Date();
          monthAgo.setDate(now.getDate() - 30);
          return callDate >= monthAgo;
        }
        return true;
      });
      
      setFilteredCalls(filteredData);
      setLoading(false);
    }, 300);
  };
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setLoading(true);
    setTabValue(newValue);
    
    setTimeout(() => {
      let sortedCalls = [...calls];
      
      if (newValue === 1) { // Højeste score
        sortedCalls.sort((a, b) => {
          const aAvg = a.segments.reduce((acc, seg) => acc + seg.score, 0) / a.segments.length;
          const bAvg = b.segments.reduce((acc, seg) => acc + seg.score, 0) / b.segments.length;
          return bAvg - aAvg;
        });
      } else if (newValue === 2) { // Laveste score
        sortedCalls.sort((a, b) => {
          const aAvg = a.segments.reduce((acc, seg) => acc + seg.score, 0) / a.segments.length;
          const bAvg = b.segments.reduce((acc, seg) => acc + seg.score, 0) / b.segments.length;
          return aAvg - bAvg;
        });
      } else if (newValue === 3) { // Længste opkald
        sortedCalls.sort((a, b) => {
          // Konverter duration til sekunder for sammenligningsformål
          const aDuration = a.duration.split(':').reduce((acc, time) => (60 * acc) + +time, 0);
          const bDuration = b.duration.split(':').reduce((acc, time) => (60 * acc) + +time, 0);
          return bDuration - aDuration;
        });
      }
      
      setFilteredCalls(sortedCalls);
      setLoading(false);
    }, 500);
  };
  
  const handlePlayClick = (id: number | string) => {
    console.log(`Play button clicked for call: ${id}`);
    
    if (playingCallId === id) {
      // Hvis det samme opkald allerede afspilles, stop det
      setPlayingCallId(null);
      console.log(`Stopped playing call: ${id}`);
    } else {
      // Ellers start afspilning af dette opkald
      setPlayingCallId(id);
      console.log(`Started playing call: ${id}`);
    }
  };
  
  const handleExpandClick = (id: number | string) => {
    console.log(`Udvid opkald med id: ${id}`);
    // Implementer udvidelse af detaljer her
  };

  // Sorteringsfunktion
  const handleSort = (field: 'date' | 'score') => {
    if (field === sortField) {
      // Hvis samme felt, skift retning
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Hvis nyt felt, sæt default retning
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Anvendt sortering på opkald
  useEffect(() => {
    let sortedCalls = [...filteredCalls];
    
    if (sortField === 'date') {
      sortedCalls.sort((a, b) => {
        const comparison = (a.timestamp || 0) - (b.timestamp || 0);
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    } else if (sortField === 'score') {
      sortedCalls.sort((a, b) => {
        const aAvg = a.segments.reduce((acc, seg) => acc + seg.score, 0) / a.segments.length;
        const bAvg = b.segments.reduce((acc, seg) => acc + seg.score, 0) / b.segments.length;
        const comparison = aAvg - bAvg;
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }
    
    setFilteredCalls(sortedCalls);
  }, [sortField, sortDirection, calls]);

  return (
    <AppContainer>
      {/* Sidebar / Menu */}
      <Sidebar>
        <Box sx={{ padding: '10px', marginBottom: 2 }}>
          <ProfileImageUpload />
        </Box>
        
        <MenuItemContainer onClick={() => handleNavigate('Dashboard')}>
          <MenuItemText>Dashboard</MenuItemText>
        </MenuItemContainer>
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          padding: '12px', 
          cursor: 'default', 
          opacity: 0.7 
        }}>
          <MenuItemText>Action Central</MenuItemText>
        </Box>
        
        <MenuItemContainer onClick={() => handleNavigate('Stats')}>
          <MenuItemText>Stats</MenuItemText>
        </MenuItemContainer>
        
        <MenuItemContainer onClick={() => handleNavigate('Calls')} sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
          <MenuItemText>Calls</MenuItemText>
        </MenuItemContainer>
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          padding: '12px', 
          cursor: 'default', 
          opacity: 0.7 
        }}>
          <MenuItemText>Team</MenuItemText>
        </Box>
        
        <MenuItemContainer onClick={() => handleNavigate('Race')}>
          <MenuItemText>Race</MenuItemText>
        </MenuItemContainer>
        
        <MenuItemContainer onClick={() => handleNavigate('Training')}>
          <MenuItemText>{t('common.training')}</MenuItemText>
        </MenuItemContainer>
        
        <Divider sx={{ my: 1 }} />
        
        <MenuItemContainer onClick={() => handleNavigate('Help')}>
          <MenuItemText>Help</MenuItemText>
        </MenuItemContainer>
        
        <MenuItemContainer onClick={() => handleNavigate('Contact')}>
          <MenuItemText>Contact</MenuItemText>
        </MenuItemContainer>
      </Sidebar>
      
      {/* Main Content */}
      <MainContent>
        {/* Header */}
        <Header>
          <Typography variant="h6">{userName || "Voice Victor"}</Typography>
        </Header>
        
        {/* Content */}
        <ContentBox>
          <Fade in={true} timeout={500}>
            <Box>
              <PageTitle variant="h4">
                {t('calls.title', 'Opkald')}
              </PageTitle>
              <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
                {t('calls.subtitle', 'Gennemgå og analysér dine samtaleresultater')}
              </Typography>
              
              {/* Search & Filters */}
              <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: { xs: 'stretch', md: 'center' } }}>
                <TextField
                  placeholder={t('calls.searchPlaceholder', 'Søg i opkald...')}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={searchQuery}
                  onChange={handleSearch}
                  sx={{ 
                    maxWidth: { xs: '100%', md: 300 },
                    backgroundColor: 'white', 
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <FilterButton 
                    variant={timeFilter === 'week' ? 'contained' : 'outlined'}
                    startIcon={<CalendarTodayIcon />}
                    onClick={() => handleTimeFilterChange('week')}
                    size="small"
                  >
                    {t('calls.thisWeek', 'Denne uge')}
                  </FilterButton>
                  
                  <FilterButton 
                    variant={timeFilter === 'month' ? 'contained' : 'outlined'}
                    startIcon={<CalendarTodayIcon />}
                    onClick={() => handleTimeFilterChange('month')}
                    size="small"
                  >
                    {t('calls.last30Days', 'Sidste 30 dage')}
                  </FilterButton>
                  
                  <FilterButton 
                    variant={timeFilter === 'custom' ? 'contained' : 'outlined'}
                    startIcon={<CalendarTodayIcon />}
                    onClick={() => handleTimeFilterChange('custom')}
                    size="small"
                  >
                    {t('calls.custom', 'Tilpasset')}
                  </FilterButton>
                </Box>
              </Box>
              
              {/* Tabs & Cards Container */}
              <Paper sx={{ mb: 4, borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <Box>
                  <Tabs 
                    value={tabValue} 
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ 
                      borderBottom: 1, 
                      borderColor: 'divider',
                      minHeight: '48px',
                      '& .MuiTabs-indicator': {
                        backgroundColor: theme.palette.primary.main,
                      },
                      px: 2,
                      pt: 2
                    }}
                  >
                    <Tab label={t('calls.allCalls', 'Alle opkald')} />
                    <Tab label={t('calls.highestScore', 'Højeste score')} />
                    <Tab label={t('calls.lowestScore', 'Laveste score')} />
                    <Tab label={t('calls.longestCalls', 'Længste opkald')} />
                  </Tabs>
                </Box>
                
                <Box sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      {filteredCalls.length} {t('calls.callsFound', 'opkald fundet')}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <StyledBadge badgeContent={<TrendingUpIcon sx={{ fontSize: 12 }} />} color="primary">
                        <Button 
                          variant="outlined" 
                          size="small"
                          sx={{ borderRadius: '20px', textTransform: 'none' }}
                        >
                          {t('calls.improvement', 'Forbedring')}
                        </Button>
                      </StyledBadge>
                      
                      <Button 
                        variant="outlined" 
                        size="small"
                        startIcon={sortField === 'date' ? (
                          sortDirection === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />
                        ) : <ArrowDownwardIcon />}
                        onClick={() => handleSort('date')}
                        sx={{ borderRadius: '20px', textTransform: 'none' }}
                      >
                        {t('calls.date', 'Dato')}
                      </Button>
                      
                      <Button 
                        variant="outlined" 
                        size="small"
                        startIcon={sortField === 'score' ? (
                          sortDirection === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />
                        ) : <ArrowDownwardIcon />}
                        onClick={() => handleSort('score')}
                        sx={{ borderRadius: '20px', textTransform: 'none' }}
                      >
                        {t('calls.score', 'Score')}
                      </Button>
                    </Box>
                  </Box>
                  
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                      <CircularProgress />
                    </Box>
                  ) : error ? (
                    // Vis fejlbesked hvis der er en fejl
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                      <Typography variant="h6" color="error">{error}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {t('calls.errorTryAgain', 'Prøv igen senere eller kontakt support')}
                      </Typography>
                    </Box>
                  ) : filteredCalls.length > 0 ? (
                    // Vis opkald hvis der er data
                    <Box>
                      {filteredCalls.slice(0, visibleCount).map((call, index) => (
                        <Fade key={String(call.id)} in={true} timeout={300 + (index * 50)}>
                          <Box>
                            <CallCard 
                              {...call} 
                              playing={playingCallId === call.id}
                              onPlayClick={handlePlayClick}
                              onExpandClick={handleExpandClick}
                            />
                          </Box>
                        </Fade>
                      ))}
                      {visibleCount < filteredCalls.length && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                          <Button variant="outlined" onClick={() => setVisibleCount(prev => prev + 10)}>
                            {t('calls.loadMore', 'Indlæs flere')}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    // Vis besked hvis der ikke er nogen opkald
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                      <Typography variant="h6">{t('calls.noCallsFound', 'Ingen opkald fundet')}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {t('calls.tryAdjustingFilters', 'Prøv at justere dine søgekriterier')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Box>
          </Fade>
        </ContentBox>
      </MainContent>
    </AppContainer>
  );
};

export default Calls;